import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import { Button, Card } from "react-bootstrap";
import styles from './UserDetailsView.module.scss';
import { getUserDetailsById } from "../../services/User";
import { formatDateDMY } from "../../utils/dateUtils";

const formatValueWithUnit = (value, unit) => {
  if (!value || !unit) return "-";
  switch (unit.toLowerCase()) {
    case "ft_in":
      return `${value} Ft/in`;
    case "cm":
      return `${value} Cm`;
    case "kg":
      return `${value} Kg`;
    case "lb":
      return `${value} Lb`;
    default:
      return `${value} ${unit}`;
  }
};

function UserDetailsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (id) {
      (async () => {
        const res = await getUserDetailsById(id);
        if (res?.data) {
          setUser(res.data.users);
          setPlan(res.data.purchasedPlan);
        }
      })();
    }
  }, [id]);

  if (!user) return <div className="text-center mt-5">Loading user details...</div>;

  const avatarUrl = user.profilePic ? `${process.env.REACT_APP_BASEURL}/${user.profilePic}` : "/default-avatar.png";

  // Normalize plan data: API may return object or array and different field names
  let planObj = null;
  if (Array.isArray(plan)) {
    planObj = plan[0] || null;
  } else {
    planObj = plan || null;
  }

  let p = planObj
    ? {
        title:
          planObj?.planId?.name || planObj?.plan?.name || planObj?.name || planObj?.name || "-",
        description:
          planObj?.planId?.description || planObj?.plan?.description || planObj?.description || "",
        price:
          planObj?.price || planObj?.amount || planObj?.planId?.price || planObj?.plan?.price || "-",
        durationWeeks:
          planObj?.durationWeeks || planObj?.duration || planObj?.planId?.durationWeeks || planObj?.plan?.duration || "-",
        startDate:
          planObj?.startDate || planObj?.purchaseDate || planObj?.purchasedOn || planObj?.planId?.startDate || null,
        endDate:
          planObj?.endDate || planObj?.expiryDate || planObj?.expiresOn || planObj?.planId?.endDate || null,
        active:
          typeof planObj?.active === 'boolean' ? planObj.active : (planObj?.status === 'active'),
        paymentId:
          planObj?.paymentId || planObj?.orderId || planObj?.transactionId || planObj?.payment_id || "-",
      }
    : null;

  // If no plan from API, use static demo data so UI always shows details
  let isDemo = false;
  // if (!p) {
  //   isDemo = true;
  //   p = {
  //     title: "Starter Plan",
  //     description: "Monthly starter subscription - access to basic features",
  //     price: 299,
  //     durationWeeks: 4,
  //     startDate: "2025-09-01",
  //     endDate: "2025-09-30",
  //     isActive: false,
  //     paymentId: "DEMO-0001",
  //   };
  // }

  return (
    <div className="container py-4">
      <PageHeader1
        pagetitle="User Details"
        navigation="Users"
        navlink="/user-list"
        current="Details"
      />

      <div className="row">
        <div className="col-lg-12">
          <Card className={`${styles.containerCard}`}>
            <div className={styles.headerRow}>
              <div className={styles.avatar}>
                <img src={avatarUrl} alt="Profile" />
              </div>
              <div>
                <div className={styles.userName}>{user.first_name || '-'}</div>
                <div className={styles.userMeta}>{user.email || '-'}</div>
                <div className={styles.userMeta}>{user.mobile || '-'}</div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">User Info</h5>
                <div className={styles.infoRow}><span className={styles.infoLabel}>Name:</span> <span className={styles.infoValue}>{user.first_name || '-'} {user.last_name || '-'}</span></div>
                <div className={styles.infoRow}><span className={styles.infoLabel}>Email Id:</span> <span className={styles.infoValue}>{user.email || '-'}</span></div>
                <div className={styles.infoRow}><span className={styles.infoLabel}>Mobile No.:</span> <span className={styles.infoValue}>{user.mobile || '-'}</span></div>
                <div className={styles.infoRow}><span className={styles.infoLabel}>Address:</span> <span className={styles.infoValue}>{user.location || '-'}</span></div>
                
              </div>

              <div className="col-md-6">
                <h5 className="mb-3">Purchased Plan</h5>
                <div className={styles.planCard}>
                  {p ? (
                    <div className={styles.planDetails}>
                      <div className={styles.planRow}>
                        <div>
                          <div className={styles.planTitle}>{p.title || '-'}</div>
                          <div className={styles.planMeta}>{p.description || ''}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div className={styles.planPrice}>${p.price || '-'}</div>
                          <div className="mt-1"><span className={styles.planBadge}>{p.durationWeeks} wk</span></div>
                        </div>
                      </div>

                      <div className={styles.planGrid}>
                        <div>
                          <div className="text-muted">Purchased On</div>
                          <div className={styles.planMeta}>{p.startDate ? formatDateDMY(p.startDate) : '-'}</div>
                        </div>
                        <div>
                          <div className="text-muted">Expiry Date</div>
                          <div className={styles.planMeta}>{p.endDate ? formatDateDMY(p.endDate) : '-'}</div>
                        </div>
                        <div>
                          <div className="text-muted">Status</div>
                          <div className={styles.planMeta}>{p.active ? 'Active' : 'Inactive'}</div>
                        </div>
                        {/* <div>
                          <div className="text-muted">Payment Id</div>
                          <div className={styles.planMeta}>{p.paymentId || '-'}</div>
                        </div> */}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.emptyPlanCenter}>
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      <div>No plan purchased yet.</div>
                      {plan && (
                        <pre style={{marginTop:12,background:'#f8fafc',padding:12,borderRadius:6,overflow:'auto',maxHeight:200}}>
                          {JSON.stringify(plan, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.backWrapper}>
              <Button className={styles.backBtn} onClick={() => navigate("/user-list") }>
                Back
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div className="mb-2">
    <strong>{label}:</strong> <span className="ms-2">{value || "-"}</span>
  </div>
);

export default UserDetailsView;
